/* eslint-disable max-lines */
import React, { useRef } from 'react';
import { PercentIcon } from '@fronton/icons-react';
import cn from 'clsx';
import {
  CardLinkWithGAClickButton,
  CardMedia,
  MediaCard,
  MediaCardContent,
  MediaCardTitle,
  Link,
  LinkWithGAClickButton,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { useIntersection } from 'common/hooks';
import { strictBreakpoints } from 'common/styles/breakpoints';
import { RELATED_SERVICES_STORE_NAME, RelatedService, selectActiveServices } from 'modules/related-services';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { analyticsUtils } from 'utils/analytics-utils';
import { createElementKey } from 'utils';
import { BATHROOM_ELEMENT_BLOCK } from '../../../bathroom-constants';
import {
  REPAIR_IMAGE,
  REPAIR_YOURSELF_CARD_LIST,
} from './new-bathroom-repair-constants';
import {
  newBathroomRepairProfessional,
  newBathroomRepairProfessionalCard,
  newBathroomRepairProfessionalCardContent,
  newBathroomRepairProfessionalCardDescription,
  newBathroomRepairProfessionalCardImage,
  newBathroomRepairProfessionalCardTitle,
  newBathroomRepairProfessionalService,
  newBathroomRepairProfessionalServicePrice,
  newBathroomRepairProfessionalServiceText,
  newBathroomRepairProfessionalTitle,
  newBathroomRepairYourself,
  newBathroomRepairYourselfCards,
  newBathroomRepairYourselfCardContent,
  newBathroomRepairYourselfCardTitle,
  newBathroomRepairServiceDisclaimerGrid,
  newBathroomRepairServiceDisclaimer,
  newBathroomRepairServiceGrid,
} from './new-bathroom-repair-styles';

type NewBathroomRepairProps = {
  regionId: string;
};

export const NewBathroomRepair: React.FC<NewBathroomRepairProps> = ({ regionId }) => {
  const productRef = useRef<HTMLAnchorElement[]>([]);
  const { relatedServices } = useStoreon<State>(RELATED_SERVICES_STORE_NAME);
  const activeServices = selectActiveServices(relatedServices) || [];

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');

    if (!position) {
      return;
    }

    const numberPosition = Number(position);
    const relatedService = activeServices[numberPosition];

    analyticsUtils.sendEvent('GA:impressions', {
      elementBlock: `${BATHROOM_ELEMENT_BLOCK} / services`,
      elementType: 'product',
      productName: relatedService.title,
      productId: relatedService.categoryId,
      productPrice: relatedService.price,
      productPosition: numberPosition + 1,
      productBelong: 'LeroyMerlinServices',
    });
  });

  const handleServiceClick = (relatedService: RelatedService, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementBlock: `${BATHROOM_ELEMENT_BLOCK} / services`,
      elementType: 'product',
      productName: relatedService.title,
      productId: relatedService.categoryId,
      productPrice: relatedService.price,
      productPosition: position + 1,
      productBelong: 'LeroyMerlinServices',
    });
  };

  return (
    <Section>
      <div className={newBathroomRepairProfessional}>
        <SectionSubTitle
          as={'h2'}
          className={newBathroomRepairProfessionalTitle}
          margin="m"
          variant={'h2'}
        >
          Доверьте ремонт профессионалам
        </SectionSubTitle>
        <LinkWithGAClickButton
          className={newBathroomRepairProfessionalCard}
          elementBlock="landingVannuyuKomnatu"
          elementName={'Доверьте ремонт профессионалам'}
          elementText="Сделаем ремонт быстро качественно"
          href="/uslugi/remont-vannoy-komnaty/"
          title="Ремонт ванной комнаты"
        >
          <div className={newBathroomRepairProfessionalCardContent}>
            <Typography className={newBathroomRepairProfessionalCardTitle} variant="h3">
              Сделаем комплексный ремонт быстро и качественно
            </Typography>
            <Typography className={newBathroomRepairProfessionalCardDescription} size="body_long" variant="m">
              Гарантия 12 месяцев на все работы
            </Typography>
          </div>
          <picture>
            <source
              srcSet={REPAIR_IMAGE.srcLd}
              media={strictBreakpoints['--only-largeDesktop']}
            />
            <source
              srcSet={REPAIR_IMAGE.srcD}
              media={strictBreakpoints['--only-desktop']}
            />
            <source
              srcSet={REPAIR_IMAGE.srcT}
              media={strictBreakpoints['--only-tablet']}
            />
            <img
              className={newBathroomRepairProfessionalCardImage}
              alt="Изображение работника"
              src={REPAIR_IMAGE.srcM}
            />
          </picture>
        </LinkWithGAClickButton>

        <div className={newBathroomRepairServiceGrid}>
          {activeServices.map((service, index) => (
            <>
              <Link
                className={newBathroomRepairProfessionalService}
                data-position={index}
                onClick={() => handleServiceClick(service, index)}
                href={service.serviceURL.replace('$1', regionId)}
                refCb={(el: HTMLAnchorElement | null) => {
                  const position = el?.getAttribute('data-position');
                  if (position && el) {
                    productRef.current[+position] = el;
                  }
                }}
                title="Оформление заказа"
              >
                <img alt="Иконка услуги" src={service.imageUrl} width={24} height={24} />
                <Typography className={newBathroomRepairProfessionalServiceText} size="body_long" variant="s">
                  {service.title}
                </Typography>

                <Typography adaptive={false} className={newBathroomRepairProfessionalServicePrice} variant="s" size="body_accent" as="p">
                  от {service.price} ₽
                </Typography>
              </Link>

              {index === 0 && (
                <div
                  className={cn(
                    newBathroomRepairServiceDisclaimer,
                    newBathroomRepairServiceDisclaimerGrid,
                  )}
                >
                  <PercentIcon type="outline" size="s" color="service-dark" />
                  <Typography variant="minor_caption" color="service-dark">
                    Компенсируем стоимость замера при заказе установки в магазине
                  </Typography>
                </div>
              )}
            </>
          ))}
        </div>
      </div>

      <div>
        <SectionSubTitle as="h3" className={newBathroomRepairYourself} margin="m" variant="h3">
          Попробуйте сделать своими руками
        </SectionSubTitle>

        <div className={newBathroomRepairYourselfCards}>
          {REPAIR_YOURSELF_CARD_LIST.map(({ link, media, title }, index) => (
            <CardLinkWithGAClickButton
              elementBlock={BATHROOM_ELEMENT_BLOCK}
              elementName="Попробуйте сделать своими руками"
              elementText={title}
              href={link.href}
              title={link.title}
              key={createElementKey('repair-yourself--card', index)}
              rel="noreferrer"
            >
              <MediaCard>
                <CardMedia
                  loading="lazy"
                  src={media.src}
                  alt={media.alt}
                />
                <MediaCardContent className={newBathroomRepairYourselfCardContent}>
                  <MediaCardTitle className={newBathroomRepairYourselfCardTitle}>{title}</MediaCardTitle>
                </MediaCardContent>
              </MediaCard>
            </CardLinkWithGAClickButton>
          ))}
        </div>
      </div>
    </Section>
  );
}
